/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import CircleIcon from '@mui/icons-material/Circle';
import { Box, Typography } from '@mui/material';
import { useContext, useEffect, type ReactNode } from 'react';
import { type SxProps } from '@mui/material/styles';

import type { SidebarComponentProps } from '../../constants/tabs';
import { LanguageSelection } from '../LanguageSelection/LanguageSelection';
import { PluginPremiumBadge } from '../PluginBadges/PluginBadges';
import { getCapabilitiesForView } from '../../utils/getCapabilitiesForView/getCapabilitiesForView';
import { SidebarSectionHeader } from '../SidebarSectionHeader/SidebarSectionHeader';
import { GTMContext } from '../../contexts/GTMContext';
import type { EditorLanguage } from '../LanguageSelection/languages';
import { GTM_EVENT_INSTALLATION_SUMMARY, GTM_EVENT_LANGUAGE } from '../../constants/GTMEventNames';
import type { PresetView } from '../../utils/getPresets/getPresets';
import { triggerStatsEvent } from '../../utils/triggerStatsEvent/triggerStatsEvent';
import { PremiumFeaturesSection } from './PremiumFeaturesSection/PremiumFeaturesSection';
import { SESSION_ID } from '../../constants/sessionId';

import {
	ImageInstallationEditorBalloon,
	ImageInstallationEditorBalloonStarter,
	ImageInstallationEditorClassic,
	ImageInstallationEditorClassicStarter,
	ImageInstallationEditorDecoupled,
	ImageInstallationEditorDocumentStarter,
	ImageInstallationEditorInline,
	ImageInstallationEditorInlineStarter,
	ImageInstallationPresetArticle,
	ImageInstallationPresetDocument,
	ImageInstallationPresetFeatureRich,
	ImageInstallationPresetHtml,
	ImageInstallationPresetMicro,
	ImageInstallationPresetNotion
} from '../../assets';
import { EditorSettingsContext } from '../../contexts/EditorSettingsContext';
import { CustomerPortalContext } from '../../contexts/CustomerPortalContext';

const boxBorder: SxProps = {
	borderRadius: '4px',
	borderStyle: 'solid',
	borderWidth: '1px',
	borderColor: 'secondary.100'
};

export function Installation( {
	activeTab,
	isSelectedPresetModified,
	onTabChange
}: SidebarComponentProps ): ReactNode {
	const {
		editorType,
		preset: selectedPreset,
		editorPlugins,
		editorLanguage,
		toolbarOptions,
		capabilities,
		onEditorPluginsDeselect,
		onEditorLanguageChange
	} = useContext( EditorSettingsContext );
	const { triggerGTMEvent } = useContext( GTMContext );
	const { emitCustomerPortalEvent, isLoggedIn } = useContext( CustomerPortalContext );

	const capabilitiesParsed = getCapabilitiesForView( { editorType, capabilities, editorPlugins, searchQuery: '', view: 'advanced' } );
	const selectedFeatures = capabilitiesParsed
		.flatMap( capability => capability.features )
		.filter( feature => feature.isSelected || feature.isSelectedIndeterminate );

	const selectedFreeFeaturesCount = selectedFeatures.filter( feature => !feature.isPremium ).length;
	const selectedPremiumFeatures = selectedFeatures.filter( feature => feature.isPremium );
	const selectedPremiumFeaturesCount = selectedPremiumFeatures.length;

	const selectedEditorDescription = selectedPremiumFeaturesCount + selectedFreeFeaturesCount === 0 ?
		<span>You've selected <b>{ selectedPreset?.editorType }</b> type without any features.</span> :
		<span>You've selected <b>{ selectedPreset?.editorType }</b> type with:</span>;

	function handleLanguageChange( newLanguage: EditorLanguage ) {
		onEditorLanguageChange( newLanguage );

		triggerGTMEvent( {
			event: GTM_EVENT_LANGUAGE,
			option: newLanguage
		} );
	}

	useEffect( () => {
		if ( isLoggedIn ) {
			emitCustomerPortalEvent( { type: 'stage', stage: 'Builder Setup Incomplete' } );
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ isLoggedIn ] );

	useEffect( () => {
		const isRealTimeOn = ( capabilities.find( c => c.name === 'Collaboration' ) as any ).toggleValue;

		triggerGTMEvent( {
			event: GTM_EVENT_INSTALLATION_SUMMARY,
			option: selectedPreset!.name,
			isDirty: isSelectedPresetModified,
			realTime: isRealTimeOn,
			menuBar: toolbarOptions.isMenuBarUsed,
			wrapped: toolbarOptions.isCollapsable,
			additionalBlock: toolbarOptions.isBlockToolbarOn,
			additionalBalloon: toolbarOptions.isBalloonToolbarOn,
			capability: capabilitiesParsed.map( capability => {
				return {
					name: capability.name,
					selected: capability.features.filter( f => f.isSelected || f.isSelectedIndeterminate ).length,
					total: capability.features.length
				};
			} )
		} );

		triggerStatsEvent( {
			event_type: GTM_EVENT_INSTALLATION_SUMMARY,
			event_data: {
				session_id: SESSION_ID,
				preset: selectedPreset!.name,
				is_dirty: isSelectedPresetModified,
				editor_plugins: editorPlugins,
				real_time: isRealTimeOn,
				menu_bar: toolbarOptions.isMenuBarUsed,
				wrapped: toolbarOptions.isCollapsable,
				additional_block: toolbarOptions.isBlockToolbarOn,
				additional_balloon: toolbarOptions.isBalloonToolbarOn
			},
			// @ts-expect-error ENVIRONMENT variable is injected by vite.
			environment: ENVIRONMENT
		} );
	}, [] ); // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<Box>
			<Box>
				<SidebarSectionHeader
					title={ activeTab.tabName }
					description={ activeTab.description }
				/>
			</Box>
			<Box sx={ { ...boxBorder, marginTop: '16px', padding: '12px' } }>
				<Box>
					<Typography color={ 'text.primary' } fontSize={ '14px' } fontWeight={ 700 } lineHeight={ '22px' }>
						Editor language
					</Typography>
					<Typography color={ 'text.secondary' } fontSize={ '12px' } fontWeight={ 400 } lineHeight={ '20px' }>
						Choose the language of the editor.
					</Typography>
				</Box>
				<Box marginTop='12px' position='relative'>
					<LanguageSelection activeEditorLanguage={ editorLanguage } setLocalEditorLanguage={ handleLanguageChange }/>
				</Box>
			</Box>
			<Box sx={ { ...boxBorder, marginTop: '16px', padding: '8px' } }>
				<Box>
					<Typography color={ 'text.primary' } fontSize={ '14px' } fontWeight={ 700 } lineHeight={ '22px' }>
						Selected editor
					</Typography>
					<Typography color={ 'text.secondary' } fontSize={ '12px' } fontWeight={ 400 } lineHeight={ '20px' }>
						A summary of the editor you built in the previous steps.
					</Typography>
				</Box>
				<Box sx={ { ...boxBorder, display: 'flex', marginTop: '12px', padding: '8px', gap: '8px' } }>
					{ selectedPreset && (
						<Box sx={ { display: 'flex', alignItems: 'center' } } >
							<img style={ { height: '106px' } } src={ getImageForSelectedPreset( selectedPreset ) }/>
						</Box>
					) }
					<Box>
						<Typography
							color={ 'text.primary' }
							fontSize={ '14px' }
							fontWeight={ 700 }
							lineHeight={ '22px' }
							data-testid={ 'selected-preset-name' }
						>
							{ selectedPreset?.name }
						</Typography>
						<Typography
							color={ 'text.secondary' }
							fontSize={ '12px' }
							fontWeight={ 400 }
							lineHeight={ '20px' }
							data-testid={ 'selected-editor-description' }
						>
							{ selectedEditorDescription }
						</Typography>
						{ selectedPremiumFeaturesCount > 0 && (
							<Box sx={ { display: 'flex', alignItems: 'center', gap: '3px' } } data-testid={ 'selected-premium-features' }>
								<StylizedCircleIcon/>
								<Typography color={ 'text.primary' } fontSize={ '12px' } fontWeight={ 700 } lineHeight={ '20px' }>
									{ selectedPremiumFeaturesCount } premium feature{ selectedPremiumFeaturesCount > 1 && 's' }.
								</Typography>
								<PluginPremiumBadge onlyIcon/>
							</Box>
						) }
						{ selectedFreeFeaturesCount > 0 && (
							<Box sx={ { display: 'flex', alignItems: 'center', gap: '3px' } } data-testid={ 'selected-free-features' }>
								<StylizedCircleIcon/>
								<Typography color={ 'text.secondary' } fontSize={ '12px' } fontWeight={ 400 } lineHeight={ '20px' }>
									{ selectedFreeFeaturesCount } free feature{ selectedFreeFeaturesCount > 1 && 's' }.
								</Typography>
							</Box>
						) }
					</Box>
				</Box>
				<PremiumFeaturesSection
					onTabChange={ onTabChange }
					onEditorPluginsDeselect={ onEditorPluginsDeselect }
					selectedPremiumFeatures={ selectedPremiumFeatures }
				/>
			</Box>
		</Box>
	);
}

function StylizedCircleIcon(): ReactNode {
	return (
		<CircleIcon
			sx={ {
				width: '8px', height: '8px', color: 'primary.main'
			} }
		/>
	);
}

function getImageForSelectedPreset( selectedPreset: PresetView ): string {
	const images: Record<string, string> = {
		'Collaborative Document Editor': ImageInstallationPresetDocument,
		'Collaborative Article Editor': ImageInstallationPresetArticle,
		'Feature-rich Editor': ImageInstallationPresetFeatureRich,
		'Notion-like Editor': ImageInstallationPresetNotion,
		'HTML Editor': ImageInstallationPresetHtml,
		'Microeditor': ImageInstallationPresetMicro,
		'Classic Editor': ImageInstallationEditorClassic,
		'Document Editor': ImageInstallationEditorDecoupled,
		'Inline Editor': ImageInstallationEditorInline,
		'Balloon Editor': ImageInstallationEditorBalloon,
		'Balloon Block Editor': ImageInstallationEditorBalloon,
		'Classic Editor (starter)': ImageInstallationEditorClassicStarter,
		'Document Editor (starter)': ImageInstallationEditorDocumentStarter,
		'Inline Editor (starter)': ImageInstallationEditorInlineStarter,
		'Balloon Editor (starter)': ImageInstallationEditorBalloonStarter
	};

	return images[ selectedPreset.name ];
}
